/**
 * Kaizen Sports — Premium Brand JavaScript v3.0
 * Hero animation · Stat counters · Marquee · Parallax · Scroll reveals
 */

(function () {
  'use strict';

  /* ─────────────────────────────────────────────────────────────────────────
     1. PAGE LOAD ANIMATION
     ───────────────────────────────────────────────────────────────────────── */
  function initPageLoad() {
    document.documentElement.classList.add('kz-page-loading');
    function reveal() {
      document.documentElement.classList.remove('kz-page-loading');
      document.documentElement.classList.add('kz-page-ready');
    }
    if (document.readyState === 'complete') {
      setTimeout(reveal, 80);
    } else {
      window.addEventListener('load', function () { setTimeout(reveal, 80); });
    }
  }

  /* ─────────────────────────────────────────────────────────────────────────
     2. TRANSPARENT HEADER
     ───────────────────────────────────────────────────────────────────────── */
  function initTransparentHeader() {
    var body = document.body;
    var headerSection = document.getElementById('shopify-section-header');
    if (!headerSection) return;

    function updateHeaderHeight() {
      document.documentElement.style.setProperty(
        '--kz-header-height',
        headerSection.offsetHeight + 'px'
      );
    }

    updateHeaderHeight();
    window.addEventListener('resize', updateHeaderHeight);

    var ticking = false;
    function onScroll() {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(function () {
        body.classList.toggle('body-scrolled', window.scrollY > 60);
        ticking = false;
      });
    }

    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
  }

  /* ─────────────────────────────────────────────────────────────────────────
     3. SCROLL REVEAL
     ───────────────────────────────────────────────────────────────────────── */
  function initScrollReveal() {
    var elements = document.querySelectorAll('[data-kz-reveal]');
    if (!elements.length) return;

    if (!window.IntersectionObserver) {
      elements.forEach(function (el) { el.classList.add('kz-revealed'); });
      return;
    }

    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        var d = entry.target.dataset.kzDelay;
        if (d) entry.target.style.transitionDelay = (parseInt(d, 10) * 0.08) + 's';
        entry.target.classList.add('kz-revealed');
        observer.unobserve(entry.target);
      });
    }, { threshold: 0.1, rootMargin: '0px 0px -50px 0px' });

    elements.forEach(function (el) { observer.observe(el); });
  }

  /* ─────────────────────────────────────────────────────────────────────────
     4. HERO SLIDE ANIMATION
     Text slides up on each slide change. Ken Burns applied via CSS class swap.
     ───────────────────────────────────────────────────────────────────────── */
  function initHeroAnimation() {
    var slideshow = document.querySelector('.slideshow');
    if (!slideshow) return;

    var textSelectors = ['.banner__heading', '.banner__text', '.banner__buttons'];
    var delays         = ['0.1s', '0.32s', '0.52s'];

    // Add kz-anim-ready to ALL banner text elements so they start hidden
    textSelectors.forEach(function (sel) {
      slideshow.querySelectorAll(sel).forEach(function (el) {
        el.classList.add('kz-anim-ready');
      });
    });

    function animateSlide(slide) {
      if (!slide) return;

      // Remove animation from all text els
      slideshow.querySelectorAll('.kz-anim-slide').forEach(function (el) {
        el.classList.remove('kz-anim-slide');
        el.style.animationDelay = '';
      });

      // Remove Ken Burns from all images
      slideshow.querySelectorAll('.kz-kb-active').forEach(function (img) {
        img.classList.remove('kz-kb-active');
      });

      // Force reflow before re-applying (restarts CSS animation)
      void slide.offsetHeight;

      // Animate this slide's text els with staggered delays
      textSelectors.forEach(function (sel, i) {
        var el = slide.querySelector(sel);
        if (!el) return;
        el.style.animationDelay = delays[i];
        el.classList.add('kz-anim-slide');
      });

      // Apply Ken Burns to this slide's hero image
      var heroImg = slide.querySelector('.banner__media img');
      if (heroImg) {
        void heroImg.offsetWidth;
        heroImg.classList.add('kz-kb-active');
      }
    }

    // Find active slide — try multiple selectors Dawn may use
    function findActive() {
      return (
        slideshow.querySelector('.slideshow__slide.is-selected') ||
        slideshow.querySelector('.slideshow__slide.is-active') ||
        slideshow.querySelector('.splide__slide.is-active') ||
        slideshow.querySelector('.slideshow__slide:first-child') ||
        slideshow.querySelector('.banner')
      );
    }

    // Initial animation after fonts/images settle
    setTimeout(function () { animateSlide(findActive()); }, 250);

    // Watch for slide class changes (Dawn toggles is-selected / is-active)
    var lastActive = null;
    new MutationObserver(function () {
      var current = findActive();
      if (current && current !== lastActive) {
        lastActive = current;
        animateSlide(current);
      }
    }).observe(slideshow, {
      attributes: true,
      subtree: true,
      attributeFilter: ['class']
    });
  }

  /* ─────────────────────────────────────────────────────────────────────────
     5. STAT COUNTER ANIMATION
     Counts up brand stat numbers when they enter the viewport
     ───────────────────────────────────────────────────────────────────────── */
  function initCounters() {
    var statEls = document.querySelectorAll('.kz-brand-story__stat-number');
    if (!statEls.length || !window.IntersectionObserver) return;

    function easeOut(t) { return 1 - Math.pow(1 - t, 3); }

    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting || entry.target.dataset.kzCounted) return;
        entry.target.dataset.kzCounted = '1';
        observer.unobserve(entry.target);

        var el    = entry.target;
        var raw   = el.textContent.trim();
        var match = raw.match(/^(\d+(?:\.\d+)?)(.*)/);
        if (!match) return;

        var target   = parseFloat(match[1]);
        var suffix   = match[2] || '';
        var duration = 2200;
        var start    = null;

        requestAnimationFrame(function step(ts) {
          if (!start) start = ts;
          var p = Math.min((ts - start) / duration, 1);
          el.textContent = Math.round(easeOut(p) * target) + suffix;
          if (p < 1) requestAnimationFrame(step);
          else el.textContent = target + suffix;
        });
      });
    }, { threshold: 0.6 });

    statEls.forEach(function (el) { observer.observe(el); });
  }

  /* ─────────────────────────────────────────────────────────────────────────
     6. MARQUEE — duplicate content for seamless infinite loop
     ───────────────────────────────────────────────────────────────────────── */
  function initMarquee() {
    document.querySelectorAll('.kz-marquee__track').forEach(function (track) {
      track.innerHTML += track.innerHTML; // duplicate for seamless loop
    });
  }

  /* ─────────────────────────────────────────────────────────────────────────
     7. HERO PARALLAX (desktop only, respects reduced-motion)
     ───────────────────────────────────────────────────────────────────────── */
  function initHeroParallax() {
    if (window.innerWidth < 990) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    var hero = document.querySelector('.template-index .slideshow');
    if (!hero) return;

    var ticking = false;
    window.addEventListener('scroll', function () {
      var scrollY = window.pageYOffset;
      if (scrollY > hero.offsetHeight || ticking) return;
      ticking = true;
      requestAnimationFrame(function () {
        hero.querySelectorAll('.banner__media').forEach(function (media) {
          media.style.transform = 'translateY(' + (scrollY * 0.2) + 'px)';
        });
        ticking = false;
      });
    }, { passive: true });
  }

  /* ─────────────────────────────────────────────────────────────────────────
     8. SECTION HEADING REVEALS (Dawn built-in section headings)
     ───────────────────────────────────────────────────────────────────────── */
  function initHeadingReveals() {
    var headings = document.querySelectorAll(
      '.featured-collection .title, .collection-list-wrapper .title'
    );
    if (!headings.length || !window.IntersectionObserver) return;

    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('kz-heading-revealed');
        observer.unobserve(entry.target);
      });
    }, { threshold: 0.2 });

    headings.forEach(function (el) {
      el.classList.add('kz-heading-reveal');
      observer.observe(el);
    });
  }

  /* ─────────────────────────────────────────────────────────────────────────
     9. CARD IMAGE PRELOAD (secondary image on hover)
     ───────────────────────────────────────────────────────────────────────── */
  function initSecondaryImagePreload() {
    document.querySelectorAll('.card--product .card__inner').forEach(function (card) {
      card.addEventListener('mouseenter', function () {
        var imgs = card.querySelectorAll('img');
        if (imgs.length > 1) {
          var src = imgs[1].dataset.src || imgs[1].src;
          if (src) (new Image()).src = src;
        }
      }, { once: true });
    });
  }

  /* ─────────────────────────────────────────────────────────────────────────
     10. LICENSED PANEL MOUSE PARALLAX
     ───────────────────────────────────────────────────────────────────────── */
  function initLicensedPanelEffect() {
    document.querySelectorAll('.kz-licensed-panel').forEach(function (panel) {
      panel.addEventListener('mousemove', function (e) {
        var rect = panel.getBoundingClientRect();
        var x = (e.clientX - rect.left) / rect.width  - 0.5;
        var y = (e.clientY - rect.top)  / rect.height - 0.5;
        var bg = panel.querySelector('.kz-licensed-panel__bg');
        if (bg) bg.style.transform = 'scale(1.07) translate(' + (x * -8) + 'px,' + (y * -5) + 'px)';
      });
      panel.addEventListener('mouseleave', function () {
        var bg = panel.querySelector('.kz-licensed-panel__bg');
        if (bg) bg.style.transform = '';
      });
    });
  }

  /* ─────────────────────────────────────────────────────────────────────────
     11. CARD GRID SECTION REVEAL
     Adds kz-section-visible when a featured-collection enters viewport
     ───────────────────────────────────────────────────────────────────────── */
  function initCardReveal() {
    var sections = document.querySelectorAll(
      '.shopify-section-featured-collection, [data-section-type="featured-collection"]'
    );
    if (!sections.length || !window.IntersectionObserver) {
      sections.forEach(function (s) { s.classList.add('kz-section-visible'); });
      return;
    }

    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('kz-section-visible');
        observer.unobserve(entry.target);
      });
    }, { threshold: 0.08, rootMargin: '0px 0px -60px 0px' });

    sections.forEach(function (s) { observer.observe(s); });
  }

  /* ─────────────────────────────────────────────────────────────────────────
     12. WHY CHOOSE ICON PULSE ON HOVER
     ───────────────────────────────────────────────────────────────────────── */
  function initIconPulse() {
    document.querySelectorAll('.kz-why-item').forEach(function (item) {
      var icon = item.querySelector('.kz-why-item__icon');
      if (!icon) return;
      item.addEventListener('mouseenter', function () {
        icon.classList.add('kz-icon-pulse');
      });
      icon.addEventListener('animationend', function () {
        icon.classList.remove('kz-icon-pulse');
      });
    });
  }

  /* ─────────────────────────────────────────────────────────────────────────
     INIT
     ───────────────────────────────────────────────────────────────────────── */
  initPageLoad();

  function onReady() {
    initTransparentHeader();
    initScrollReveal();
    initHeroAnimation();
    initCounters();
    initMarquee();
    initHeroParallax();
    initHeadingReveals();
    initCardReveal();
    initSecondaryImagePreload();
    initLicensedPanelEffect();
    initIconPulse();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', onReady);
  } else {
    onReady();
  }

})();
